/**
 * Lit Legion types for client-side use.
 *
 * Local mirror of canonical types from @fias/db-types — the client package
 * cannot directly import from @fias/db-types. The architecture test
 * `tests/architecture/lit-legion-status-sync.test.ts` enforces that this
 * array stays in sync with VALID_LL_TASK_STATUSES in db-types.
 *
 * Definition / submission types mirror the Zod schemas in
 * `packages/db-types/src/lit-legion-definition.ts`. The server is
 * authoritative; the client renderer narrows by `kind` and produces a
 * typed UI per element.
 */

export const LL_TASK_STATUSES = [
  'available',
  'unavailable',
  'waiting',
  'expired',
  'accepted',
  'completed',
  'pending',
  'awarded',
] as const;

export type LLTaskStatus = (typeof LL_TASK_STATUSES)[number];

export function isLLTaskStatus(value: string): value is LLTaskStatus {
  return (LL_TASK_STATUSES as readonly string[]).includes(value);
}

/**
 * Mirror of VALID_LL_AVAILABILITY_REASONS in db-types — why a task is not
 * currently actionable, as computed by the server's availability engine.
 * Kept in sync by tests/architecture/lit-legion-status-sync.test.ts.
 */
export const LL_AVAILABILITY_REASONS = [
  'not_started',
  'expired',
  'cooldown',
  'level_too_low',
  'max_completions_reached',
  'max_retakes_reached',
] as const;

export type LLAvailabilityReason = (typeof LL_AVAILABILITY_REASONS)[number];

// =============================================================================
// Definition element types
// =============================================================================

export interface LLTextElement {
  kind: 'text';
  index: number;
  text: string;
}
export interface LLHeaderElement {
  kind: 'header';
  index: number;
  text: string;
}
export interface LLImageElement {
  kind: 'image';
  index: number;
  imageUrl: string;
}
export interface LLVideoElement {
  kind: 'video';
  index: number;
  videoUrl: string;
}
export interface LLLinkElement {
  kind: 'link';
  index: number;
  hyperlinkText: string;
  url: string;
}
export interface LLCheckboxElement {
  kind: 'checkbox';
  index: number;
  text: string;
}
export interface LLMultipleChoiceElement {
  kind: 'multipleChoice';
  index: number;
  questionType: 'question' | 'poll' | 'survey';
  questionId: string;
  prompt: string;
  options: { optionId: string; value: string }[];
}
export interface LLTextInputElement {
  kind: 'textInput';
  index: number;
  inputType: 'open' | 'tweet' | 'quiz';
  label: string;
  placeholder?: string;
  responseTag: string;
  urlInput?: string;
}
export interface LLCheckInventoryElement {
  kind: 'checkInventory';
  index: number;
  description: string;
  item: string;
  amount: number;
}
export interface LLCheckGameStateElement {
  kind: 'checkGameState';
  index: number;
  description: string;
  gameKey: string;
  gameState: string;
}

export type LLTaskElement =
  | LLTextElement
  | LLHeaderElement
  | LLImageElement
  | LLVideoElement
  | LLLinkElement
  | LLCheckboxElement
  | LLMultipleChoiceElement
  | LLTextInputElement
  | LLCheckInventoryElement
  | LLCheckGameStateElement;

export interface LLTaskDefinition {
  elements: LLTaskElement[];
  blogUrl?: string;
  videoUrl?: string;
  imageUrl?: string;
  tweetUrl?: string;
  twitterUrl?: string;
  channelUrl?: string;
  downloadUrl?: string;
  learnUrl?: string;
}

export interface LLTaskSubmission {
  checkboxes: number[];
  multipleChoices: { index: number; optionId: string }[];
  textInputs: { responseTag: string; value: string }[];
}

const REQUIRED_KINDS = new Set<LLTaskElement['kind']>([
  'checkbox',
  'multipleChoice',
  'textInput',
  'checkInventory',
  'checkGameState',
]);

/** True if the definition contains any element the player must interact with. */
export function definitionHasRequiredElements(definition: LLTaskDefinition): boolean {
  return definition.elements.some((el) => REQUIRED_KINDS.has(el.kind));
}

/**
 * Narrow a `Record<string, unknown>` from the API into a typed
 * `LLTaskDefinition`. Returns null if the input doesn't have an `elements`
 * array, which means the row is either a NULL legacy row or pre-normalization
 * legacy shape (the latter is a server-side bug — the migration should have
 * normalized everything).
 */
export function asLLTaskDefinition(
  raw: Record<string, unknown> | null | undefined,
): LLTaskDefinition | null {
  if (!raw || !Array.isArray((raw as { elements?: unknown }).elements)) {
    return null;
  }
  return raw as unknown as LLTaskDefinition;
}

/**
 * Values that count as "the event has NOT happened" inside a game-state
 * payload. Mirror of the db-types helper — keep both in sync.
 */
function isFalsyGameStateValue(value: unknown): boolean {
  if (value === false || value === null || value === undefined) return true;
  if (typeof value === 'number') return value === 0 || Number.isNaN(value);
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    // Deliberately NOT including 'no': unlike 'false' / '0', it is a plausible
    // piece of recorded game DATA (an answer, a choice) rather than a boolean
    // encoding, and reading it as "not done" would strand a player who really
    // did the thing.
    return normalized === '' || normalized === 'false' || normalized === '0';
  }
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
}

/**
 * Whether a `checkGameState` element is satisfied by the raw `data` of the
 * player's game-state row.
 *
 * `gameKey` is the row keyword the originating game writes under; `gameState`
 * is a field INSIDE that row's payload — NOT a composite `gameKey:gameState`
 * keyword (the platform's keyword pattern rejects `:`, so such a row can never
 * exist). Parses leniently because the row's `dataType` may be `text`, and
 * fails closed on anything unparseable. The field name is matched exactly —
 * see the canonical helper for why.
 *
 * Local mirror of `isGameStateFlagSatisfied` in
 * `packages/db-types/src/lit-legion-definition.ts`; the two are run over the
 * same fixtures by `tests/architecture/lit-legion-status-sync.test.ts`.
 */
export function isGameStateFlagSatisfied(
  data: string | null | undefined,
  gameState: string,
): boolean {
  if (!data) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return false;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
  if (!Object.prototype.hasOwnProperty.call(parsed, gameState)) return false;
  return !isFalsyGameStateValue((parsed as Record<string, unknown>)[gameState]);
}
